import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { config } from '../../lib/config';
import { request, requestEnvelope } from '../../shared/api/http';
import type {
  Attachment,
  AttachmentDownload,
  MessageDetail,
  MessageListData,
  MessageSummary,
} from '../../shared/api/types';
import { mailboxKeys } from '../mailbox/api';

export const messageKeys = {
  list: (mailboxId: string) => ['mailbox', mailboxId, 'messages'] as const,
  trash: (mailboxId: string) => ['mailbox', mailboxId, 'trash'] as const,
  one: (id: string) => ['message', id] as const,
};

const PAGE_SIZE = 30;

export function useMessages(mailboxId: string) {
  return useInfiniteQuery({
    queryKey: messageKeys.list(mailboxId),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const env = await requestEnvelope<MessageListData>(`/api/v1/mailboxes/${mailboxId}/messages`, {
        query: { limit: PAGE_SIZE, cursor: pageParam },
      });
      return { items: env.data.items, nextCursor: env.pagination?.nextCursor ?? null };
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
  });
}

export function useMessage(id: string) {
  return useQuery({
    queryKey: messageKeys.one(id),
    queryFn: () => request<MessageDetail>(`/api/v1/messages/${id}`),
    staleTime: 5 * 60_000,
  });
}

/**
 * Удалённые (в корзине) письма конкретного ящика — вкладка «Удалённые» (promt-74 §7f).
 * Бэкенд отдаёт корзину всего аккаунта, поэтому фильтруем по mailboxId на клиенте.
 */
export function useTrashMessages(mailboxId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: messageKeys.trash(mailboxId),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const env = await requestEnvelope<MessageListData>('/api/v1/trash/messages', {
        query: { limit: PAGE_SIZE, cursor: pageParam },
      });
      return {
        items: env.data.items.filter((m) => m.mailboxId === mailboxId),
        nextCursor: env.pagination?.nextCursor ?? null,
      };
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
  });
}

function useInvalidateMessages(mailboxId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: messageKeys.list(mailboxId) });
    void qc.invalidateQueries({ queryKey: messageKeys.trash(mailboxId) });
    void qc.invalidateQueries({ queryKey: mailboxKeys.all });
    void qc.invalidateQueries({ queryKey: mailboxKeys.one(mailboxId) });
    // Окончательное удаление освобождает израсходованный трафик — обновляем квоту (promt-79 §2).
    void qc.invalidateQueries({ queryKey: ['quota'] });
  };
}

/** Итог bulk-операции: сколько удалось, сколько нет (promt-74 §7f, режим выбора). */
export interface BulkResult {
  ok: number;
  failed: number;
}

async function runBulk(ids: string[], op: (id: string) => Promise<unknown>): Promise<BulkResult> {
  const settled = await Promise.allSettled(ids.map(op));
  return {
    ok: settled.filter((r) => r.status === 'fulfilled').length,
    failed: settled.filter((r) => r.status === 'rejected').length,
  };
}

export function useMarkRead(mailboxId: string) {
  const invalidate = useInvalidateMessages(mailboxId);
  return useMutation({
    mutationFn: (id: string) =>
      request<MessageSummary>(`/api/v1/messages/${id}/mark-read`, { method: 'PATCH' }),
    onSuccess: invalidate,
  });
}

export function useDeleteMessage(mailboxId: string) {
  const invalidate = useInvalidateMessages(mailboxId);
  return useMutation({
    mutationFn: (id: string) => request(`/api/v1/messages/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useDeleteAllMessages(mailboxId: string) {
  const invalidate = useInvalidateMessages(mailboxId);
  return useMutation({
    mutationFn: () => request(`/api/v1/mailboxes/${mailboxId}/messages/delete-all`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useMarkAllRead(mailboxId: string) {
  const invalidate = useInvalidateMessages(mailboxId);
  return useMutation({
    mutationFn: () => request(`/api/v1/mailboxes/${mailboxId}/messages/mark-all-read`, { method: 'PATCH' }),
    onSuccess: invalidate,
  });
}

/** Восстановить письмо из корзины (PATCH /messages/{id}/restore). */
export function useRestoreMessage(mailboxId: string) {
  const invalidate = useInvalidateMessages(mailboxId);
  return useMutation({
    mutationFn: (id: string) =>
      request<MessageSummary>(`/api/v1/messages/${id}/restore`, { method: 'PATCH' }),
    onSuccess: invalidate,
  });
}

/** Массовое удаление выбранных писем (режим выбора, promt-74 §7f). */
export function useDeleteMessages(mailboxId: string) {
  const invalidate = useInvalidateMessages(mailboxId);
  return useMutation({
    mutationFn: (ids: string[]) =>
      runBulk(ids, (id) => request(`/api/v1/messages/${id}`, { method: 'DELETE' })),
    onSuccess: invalidate,
  });
}

/**
 * Окончательное удаление письма из корзины (DELETE /messages/{id}/permanent).
 * Строка и вложения удаляются физически: письмо становится недоступным по прямой
 * ссылке, пропадает из корзины и освобождает место в лимите трафика (promt-79 §2).
 */
export function usePermanentDeleteMessage(mailboxId: string) {
  const invalidate = useInvalidateMessages(mailboxId);
  return useMutation({
    mutationFn: (id: string) => request(`/api/v1/messages/${id}/permanent`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Массовое окончательное удаление выбранных писем из корзины (promt-79 §2). */
export function usePermanentDeleteMessages(mailboxId: string) {
  const invalidate = useInvalidateMessages(mailboxId);
  return useMutation({
    mutationFn: (ids: string[]) =>
      runBulk(ids, (id) => request(`/api/v1/messages/${id}/permanent`, { method: 'DELETE' })),
    onSuccess: invalidate,
  });
}

/** Массовая отметка «прочитано» для выбранных писем (режим выбора). */
export function useMarkReadMany(mailboxId: string) {
  const invalidate = useInvalidateMessages(mailboxId);
  return useMutation({
    mutationFn: (ids: string[]) =>
      runBulk(ids, (id) => request(`/api/v1/messages/${id}/mark-read`, { method: 'PATCH' })),
    onSuccess: invalidate,
  });
}

/** Массовое восстановление выбранных писем из корзины (вкладка «Удалённые»). */
export function useRestoreMessages(mailboxId: string) {
  const invalidate = useInvalidateMessages(mailboxId);
  return useMutation({
    mutationFn: (ids: string[]) =>
      runBulk(ids, (id) => request(`/api/v1/messages/${id}/restore`, { method: 'PATCH' })),
    onSuccess: invalidate,
  });
}

export function useAttachmentDownload() {
  return useMutation({
    mutationFn: (id: string) => request<AttachmentDownload>(`/api/v1/attachments/${id}`),
  });
}

/** Presigned-URL живёт 5 минут — перезапрашиваем чуть раньше, чтобы картинки не «отваливались». */
const INLINE_URL_STALE_MS = 4 * 60_000;

/**
 * URL'ы встроенных картинок письма: ключ — MIME Content-ID, значение — presigned-URL
 * с `Content-Disposition: inline`. Тело письма ссылается на них как `src="cid:<contentId>"`.
 */
export function useInlineAttachmentUrls(messageId: string, attachments: Attachment[] | undefined) {
  const inline = (attachments ?? []).filter(
    (a): a is Attachment & { contentId: string } => a.isInline && Boolean(a.contentId)
  );

  return useQuery({
    queryKey: [...messageKeys.one(messageId), 'inline', inline.map((a) => a.id).join(',')],
    enabled: inline.length > 0,
    staleTime: INLINE_URL_STALE_MS,
    gcTime: INLINE_URL_STALE_MS,
    queryFn: async () => {
      const entries = await Promise.all(
        inline.map(async (a) => {
          const res = await request<AttachmentDownload>(`/api/v1/attachments/${a.id}`, {
            query: { inline: true },
          });
          return [a.contentId, res.url] as const;
        })
      );
      return Object.fromEntries(entries);
    },
  });
}

/** Получить presigned-URL на сырое RFC822 (.eml) письма. */
export function useMessageRawDownload() {
  return useMutation({
    mutationFn: (id: string) => request<AttachmentDownload>(`/api/v1/messages/${id}/raw`),
  });
}

/** Скачать все вложения письма одним zip-архивом (серверная сборка). */
export function useDownloadAttachmentsArchive() {
  return useMutation({
    mutationFn: async (messageId: string) => {
      const res = await fetch(`${config.apiBase}/api/v1/messages/${messageId}/attachments/archive`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`archive request failed: ${String(res.status)}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attachments-${messageId}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
  });
}
